import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSyncConfigured } from '../../hooks/useSyncStatus';
import { Toasts } from '../../shell/Toasts';
import { Button, ButtonVariant } from '../../ui/Button';
import { AccountHome } from '../sync/AccountHome';
import { GenerateFlow } from '../sync/GenerateFlow';
import { JoinFlow } from '../sync/JoinFlow';
import { LoginFlow } from '../sync/LoginFlow';
import { RegisterFlow } from '../sync/RegisterFlow';
import { ResetFlow } from '../sync/ResetFlow';
import { ServerUrlField } from '../sync/ServerUrlField';
import syncStyles from '../sync/Sync.module.css';
import { currentOrigin, isValidServerUrl } from '../sync/urls';
import { useSyncManager } from '../sync/useSyncManager';
import { EntryKind } from './gateModel';
import { LocalDataNotice } from './LocalDataNotice';
import { LocalModeEntry } from './LocalModeEntry';
import styles from './Welcome.module.css';

const View = {
  Home: 'home',
  Generate: 'generate',
  Join: 'join',
  Register: 'register',
  Login: 'login',
  Reset: 'reset',
} as const;
type View = (typeof View)[keyof typeof View];

interface WelcomePageProps {
  /** Records already on this device; the notice is shown when above zero. */
  readonly localCount: number;
  /** Sync is on and the user has finished the flow (including the recovery code after registering). */
  readonly onEntered: (kind: EntryKind) => void;
}

/** The full-screen page of a browser without sync: sign in, register or use a passphrase, then enter the app. */
export function WelcomePage({ localCount, onEntered }: WelcomePageProps) {
  const { t } = useTranslation();
  const { manager, failed, retry } = useSyncManager();
  const configured = useSyncConfigured();
  const [view, setView] = useState<View>(View.Home);
  const [serverUrl, setServerUrl] = useState(() => currentOrigin());
  const [afterReset, setAfterReset] = useState<string | null>(null);
  const [unsupportedUrl, setUnsupportedUrl] = useState<string | null>(null);
  const accountsUnsupported = unsupportedUrl !== null && unsupportedUrl === serverUrl;

  // Registration turns sync on before the user has seen the recovery code: that view ends itself.
  useEffect(() => {
    if (!configured || view === View.Register) return;
    onEntered(view === View.Login || view === View.Join ? EntryKind.Existing : EntryKind.New);
  }, [configured, view, onEntered]);

  const open = (next: View): void => {
    setAfterReset(null);
    setView(next);
  };
  const home = (): void => setView(View.Home);
  const onUnsupported = (): void => {
    setUnsupportedUrl(serverUrl);
    setView(View.Home);
  };
  const urlOk = isValidServerUrl(serverUrl);

  return (
    <main className={styles.page} data-testid="welcome-gate">
      <div className={styles.column}>
        <header>
          <h1 className={styles.title}>{t('app.name')}</h1>
          <p className={styles.tagline}>{t('welcome.tagline')}</p>
        </header>
        {localCount > 0 ? <LocalDataNotice count={localCount} /> : null}
        <div className={styles.card}>
          {manager === null ? (
            failed ? (
              <div className={syncStyles.flow}>
                <p className={syncStyles.errorText} role="alert">
                  {t('sync.loadError')}
                </p>
                <div>
                  <Button onClick={retry}>{t('welcome.firstSync.retry')}</Button>
                </div>
              </div>
            ) : null
          ) : view === View.Register ? (
            <RegisterFlow
              manager={manager}
              serverUrl={serverUrl}
              onBack={home}
              onDone={() => onEntered(EntryKind.New)}
              onUnsupported={onUnsupported}
            />
          ) : view === View.Generate ? (
            <GenerateFlow manager={manager} serverUrl={serverUrl} onBack={home} />
          ) : view === View.Join ? (
            <JoinFlow manager={manager} serverUrl={serverUrl} onBack={home} />
          ) : view === View.Login ? (
            <LoginFlow
              manager={manager}
              serverUrl={serverUrl}
              initialEmail={afterReset ?? ''}
              notice={afterReset === null ? undefined : t('sync.account.reset.done')}
              onBack={home}
              onUnsupported={onUnsupported}
            />
          ) : view === View.Reset ? (
            <ResetFlow
              manager={manager}
              serverUrl={serverUrl}
              onBack={home}
              onReset={(email) => {
                setAfterReset(email);
                setView(View.Login);
              }}
              onUnsupported={onUnsupported}
            />
          ) : (
            <div className={syncStyles.flow} data-testid="welcome-home">
              <ServerUrlField value={serverUrl} onChange={setServerUrl} />
              {accountsUnsupported ? (
                <p className={syncStyles.notice} role="status" data-testid="account-unsupported">
                  {t('sync.account.unsupported')}
                </p>
              ) : (
                <AccountHome
                  disabled={!urlOk}
                  onLogin={() => open(View.Login)}
                  onRegister={() => open(View.Register)}
                  onForgot={() => open(View.Reset)}
                />
              )}
              <div className={syncStyles.divider}>
                <h3 className={syncStyles.dividerTitle}>{t('sync.account.passphraseTitle')}</h3>
                <div className={syncStyles.actions}>
                  <Button
                    variant={ButtonVariant.Primary}
                    disabled={!urlOk}
                    onClick={() => setView(View.Generate)}
                    data-testid="sync-generate-start"
                  >
                    {t('sync.generateStart')}
                  </Button>
                  <Button disabled={!urlOk} onClick={() => setView(View.Join)} data-testid="sync-join-start">
                    {t('sync.joinStart')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        {view === View.Home ? <LocalModeEntry /> : null}
      </div>
      <Toasts />
    </main>
  );
}
